import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TablePagination,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  useTheme,
  alpha,
  Toolbar,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Delete,
  Edit,
  Send,
  Undo,
  FilterList,
  Upload,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { useProject } from '../contexts/ProjectContext';
import MainLayout from '../components/Layout/MainLayout';

const Editor = () => {
  const theme = useTheme();
  const { projectId } = useParams();
  const {
    samples,
    fetchSamples,
    fetchTaggedSamples,
    deleteSample,
    updateSampleStatus,
    bulkUpdateStatus,
    annotateSample,
  } = useData();
  const { labels, fetchLabels, bulkImportLabels } = useProject();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedSamples, setSelectedSamples] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSample, setEditingSample] = useState(null);
  const [sampleLabels, setSampleLabels] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [labelFilter, setLabelFilter] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (projectId) {
      fetchLabels(projectId);
      loadSamples();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, statusFilter]);

  const loadSamples = async () => {
    const filters = statusFilter ? { status: statusFilter } : {};
    await fetchTaggedSamples(projectId, filters);
  };

  const handleDelete = async (sampleId) => {
    if (window.confirm('Are you sure you want to delete this sample?')) {
      await deleteSample(sampleId);
      await loadSamples();
    }
  };

  const handleStatusChange = async (sampleId, newStatus) => {
    await updateSampleStatus(sampleId, newStatus);
    await loadSamples();
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedSamples.length === 0) {
      return;
    }
    await bulkUpdateStatus(selectedSamples, newStatus);
    setSelectedSamples([]);
    await loadSamples();
  };

  const handleBulkRemoveFromCortex = async () => {
    if (selectedSamples.length === 0) return;
    // Split samples by whether they have labels or not
    const withLabels = [];
    const withoutLabels = [];
    selectedSamples.forEach((id) => {
      const sample = samples.find((s) => getSampleId(s) === id);
      if (sample && sampleHasLabels(sample)) {
        withLabels.push(id);
      } else {
        withoutLabels.push(id);
      }
    });
    if (withLabels.length > 0) await bulkUpdateStatus(withLabels, 'Labeled');
    if (withoutLabels.length > 0) await bulkUpdateStatus(withoutLabels, 'Parked');
    setSelectedSamples([]);
    await loadSamples();
  };

  const handleBulkImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      await bulkImportLabels(projectId, file);
      await loadSamples();
      await fetchLabels(projectId);
    } catch (error) {
      console.error('Error uploading labels:', error);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const getSampleId = (sample) => sample.id || sample.sample_id;
  const getSampleName = (sample) => sample.name || sample.sample_name;
  const getSampleStatus = (sample) => sample.status || sample.sample_status;

  const sampleHasLabels = (sample) => {
    return sample.labels && Array.isArray(sample.labels) && sample.labels.length > 0;
  };

  const getRemoveFromCortexTarget = (sample) => {
    return sampleHasLabels(sample) ? 'Labeled' : 'Parked';
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = samples.map((sample) => getSampleId(sample));
      setSelectedSamples(allIds);
    } else {
      setSelectedSamples([]);
    }
  };

  const handleSelectSample = (sampleId) => {
    setSelectedSamples((prev) =>
      prev.includes(sampleId)
        ? prev.filter((id) => id !== sampleId)
        : [...prev, sampleId]
    );
  };

  const handleOpenEditDialog = (sample) => {
    setEditingSample(sample);

    // Initialize label values from sample
    const initialLabels = {};
    if (sample.labels && Array.isArray(sample.labels)) {
      sample.labels.forEach((labelData) => {
        // Handle both label_id and labelId field names
        const labelId = labelData.label_id || labelData.labelId;
        initialLabels[labelId] = labelData.value;
      });
    }
    setSampleLabels(initialLabels);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingSample(null);
    setSampleLabels({});
  };

  const handleSaveAnnotation = async () => {
    if (!editingSample) return;

    // Build tags array
    const tags = Object.entries(sampleLabels)
      .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
      .map(([labelId, value]) => ({
        label_id: parseInt(labelId),
        value: value,
      }));

    // Handle both id/sample_id and name/sample_name field names
    const sampleId = editingSample.id || editingSample.sample_id;
    const sampleName = editingSample.name || editingSample.sample_name;

    await annotateSample(sampleId, sampleName, tags);
    handleCloseEditDialog();
    await loadSamples();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'labeled':
        return 'success';
      case 'parked':
        return 'warning';
      case 'cortex':
        return 'info';
      case 'legacy':
        return 'default';
      default:
        return 'error';
    }
  };

  const getLabelValue = (sample, labelId) => {
    if (!sample.labels || !Array.isArray(sample.labels)) {
      return '-';
    }

    // Handle both label_id and labelId field names
    const labelData = sample.labels.find((l) =>
      (l.label_id === labelId || l.labelId === labelId)
    );

    if (!labelData) {
      return '-';
    }

    // For categorical labels, show choice name
    const label = labels.find((l) => l.id === labelId);
    if (label && label.label_type === 'Categorical' && label.choices) {
      const choice = label.choices.find((c) => c.id === labelData.value);
      return choice ? (choice.choice || choice.name) : labelData.value;
    }

    return labelData.value;
  };

  const renderLabelInput = (label) => {
    if (label.label_type === 'Categorical' && label.choices) {
      return (
        <TextField
          select
          fullWidth
          label={label.name}
          value={sampleLabels[label.id] || ''}
          onChange={(e) =>
            setSampleLabels({ ...sampleLabels, [label.id]: e.target.value })
          }
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {label.choices.map((choice) => (
            <MenuItem key={choice.id} value={choice.id}>
              {choice.choice || choice.name}
            </MenuItem>
          ))}
        </TextField>
      );
    } else {
      return (
        <TextField
          fullWidth
          type="number"
          label={label.name}
          value={sampleLabels[label.id] || ''}
          onChange={(e) =>
            setSampleLabels({ ...sampleLabels, [label.id]: e.target.value })
          }
        />
      );
    }
  };

  return (
    <MainLayout>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Sample Editor
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Label and manage your samples
          </Typography>
        </Box>

        {/* Toolbar */}
        <Card sx={{ mb: 3, borderRadius: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Filter by Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="Parked">Parked</MenuItem>
                    <MenuItem value="Labeled">Labeled</MenuItem>
                    <MenuItem value="Cortex">Cortex</MenuItem>
                    <MenuItem value="Legacy">Legacy</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <Button
                  component="label"
                  startIcon={<Upload />}
                  variant="contained"
                  disabled={uploading}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                  }}
                >
                  {uploading ? 'Uploading...' : 'Bulk Import Labels'}
                  <input
                    type="file"
                    accept=".csv"
                    hidden
                    onChange={handleBulkImport}
                  />
                </Button>
              </Grid>
              {selectedSamples.length > 0 && (
                <>
                  <Grid item>
                    <Typography variant="body2" color="text.secondary">
                      {selectedSamples.length} selected
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Button
                      size="small"
                      startIcon={<Send />}
                      variant="outlined"
                      onClick={() => handleBulkStatusChange('Cortex')}
                    >
                      Send to Cortex
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      size="small"
                      startIcon={<Undo />}
                      variant="outlined"
                      onClick={handleBulkRemoveFromCortex}
                    >
                      Remove from Cortex
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      size="small"
                      startIcon={<FilterList />}
                      variant="outlined"
                      onClick={() => handleBulkStatusChange('Parked')}
                    >
                      Move to Parked
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Table */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={
                          samples.length > 0 &&
                          selectedSamples.length === samples.length
                        }
                        indeterminate={
                          selectedSamples.length > 0 &&
                          selectedSamples.length < samples.length
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    {labels.map((label) => (
                      <TableCell key={label.id}>
                        <strong>{label.name}</strong>
                      </TableCell>
                    ))}
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {samples
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((sample) => (
                      <TableRow
                        key={getSampleId(sample)}
                        hover
                        selected={selectedSamples.includes(getSampleId(sample))}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedSamples.includes(getSampleId(sample))}
                            onChange={() => handleSelectSample(getSampleId(sample))}
                          />
                        </TableCell>
                        <TableCell>{getSampleName(sample) || 'Unnamed'}</TableCell>
                        <TableCell>
                          <Chip
                            label={getSampleStatus(sample) || 'Unknown'}
                            size="small"
                            color={getStatusColor(getSampleStatus(sample))}
                          />
                        </TableCell>
                        {labels.map((label) => (
                          <TableCell key={label.id}>
                            {getLabelValue(sample, label.id)}
                          </TableCell>
                        ))}
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEditDialog(sample)}
                            title="Edit labels"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          {getSampleStatus(sample) !== 'Cortex' && (
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleStatusChange(getSampleId(sample), 'Cortex')}
                              title="Send to Cortex"
                            >
                              <Send fontSize="small" />
                            </IconButton>
                          )}
                          {getSampleStatus(sample) === 'Cortex' && (
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleStatusChange(getSampleId(sample), getRemoveFromCortexTarget(sample))}
                              title={`Remove from Cortex → ${getRemoveFromCortexTarget(sample)}`}
                            >
                              <Undo fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(getSampleId(sample))}
                            title="Delete"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  {samples.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4 + labels.length} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">
                          No samples available. Upload data in the Dataset page.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={samples.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </CardContent>
        </Card>

        {/* Edit Annotation Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Annotate Sample: {editingSample ? (getSampleName(editingSample) || 'Unnamed') : 'Unnamed'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {labels.length === 0 ? (
                <Typography color="text.secondary">
                  No labels configured. Please add labels in Settings first.
                </Typography>
              ) : (
                labels.map((label) => (
                  <Box key={label.id}>{renderLabelInput(label)}</Box>
                ))
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>Cancel</Button>
            <Button onClick={handleSaveAnnotation} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </MainLayout>
  );
};

export default Editor;
